import mixpanel from 'mixpanel-browser'
import { flags, type FeatureDefinition } from '../../openfeature/flags'
import { HostBrowserApis } from '../../utils/host-browser-apis'
import debug from '../../utils/console'
import { isDebugEnabled } from '../../utils/debug/isDebugEnabled'
import { OpenFeature } from '@openfeature/web-sdk'
import { useOpenFeature } from '../useOpenFeature.browser'
let isMixpanelSDKInitiated = false

const flagKeys = Object.keys(flags)
type FeaturePrimitive = boolean | string | number
function mixpanelLog(name: string, eventData: Record<string, unknown> = {}) {
  window.mixpanelEventCallLog = window.mixpanelEventCallLog || []
  window.mixpanelEventCallLog.push({ name, eventData })
}

const log = debug.withTag('mixpanel')

/**
 * PageViewed can be unintentionally sent twice if Layouts are nested; this guard drops the duplicate.
 */
let sentPageViewedEvent: boolean = false
let lastPageViewedRoute: string | null = null

export type PageViewedEventReservation = {
  type: 'initial' | 'navigation'
  reserved: boolean
}

export function reservePageViewedEvent(): boolean {
  if (sentPageViewedEvent) return false
  sentPageViewedEvent = true
  return true
}

export function reserveRoutePageViewedEvent(
  route: string
): PageViewedEventReservation {
  const type =
    lastPageViewedRoute === null || lastPageViewedRoute === route
      ? 'initial'
      : 'navigation'

  if (type === 'navigation') {
    resetPageViewedDeduplication()
  }

  const reserved = reservePageViewedEvent()
  if (reserved) lastPageViewedRoute = route

  return { type, reserved }
}

/**
 * A tracker that holds a reservation but has not sent its event yet, usually
 * because it is still waiting for content to render.
 */
let cancelPendingPageView: (() => void) | null = null

export function registerPendingPageView(cancel: () => void): void {
  cancelPendingPageView = cancel
}

export function resetPageViewedDeduplication(): void {
  // Whoever resets the dedup is about to send the pageview for this load, so a
  // tracker still inside its content wait is now stale. It kept `reserved: true`
  // from before the reset and would otherwise pass the guard and send a second
  // event for the same load.
  const cancel = cancelPendingPageView
  cancelPendingPageView = null
  cancel?.()

  sentPageViewedEvent = false
}

const contextAndFlagData = new Map<string, unknown>()
async function extractFlagAndContextData() {
  if (contextAndFlagData.has('metadata')) {
    return contextAndFlagData.get('metadata')
  }
  const { client } = await useOpenFeature()
  const getValue = (key: string, defaultValue: FeaturePrimitive) => {
    if (typeof defaultValue === 'string') {
      return client.getStringValue(key, defaultValue)
    } else if (typeof defaultValue === 'boolean') {
      return client.getBooleanValue(key, defaultValue)
    } else if (typeof defaultValue === 'number') {
      return client.getNumberValue(key, defaultValue)
    }
    throw new Error(`Unsupported default value type for key: ${key}`)
  }
  const results = await Promise.all(
    flagKeys.map(async (key) => {
      try {
        const flag: FeatureDefinition<FeaturePrimitive> = flags[key]
        return {
          [key]: await getValue(key, flag.defaultValue),
        }
      } catch (error) {
        if (isDebugEnabled()) {
          log.warn(`Failed retrieving feature flag '${key}':`, error)
        }
        return {}
      }
    })
  )
  const openFeature = results.reduce((acc, result) => {
    return Object.assign(acc, result)
  }, {})

  const metadata = {
    ...openFeature,
    ...OpenFeature.getContext(),
    // eslint-disable-next-line
    ...((client as any).apiContextAccessor?.() ?? {}),
    splittestname: client.getStringValue('splittestname', 'control'),
  }
  contextAndFlagData.set('metadata', metadata)
  return metadata
}
/**
 * Set up Mixpanel SDK
 * @description
 * This function initializes the Mixpanel SDK and registers feature flags with Mixpanel.
 * It checks if the Mixpanel token is available in the runtime configuration and initializes the SDK with it.
 * It also retrieves feature flags using OpenFeature and registers them with Mixpanel.
 * The function ensures that the Mixpanel SDK is only initialized once to avoid redundant initializations.
 * @returns
 */
async function initializeMixpanelSDK() {
  if (isMixpanelSDKInitiated) return mixpanel

  const config = useRuntimeConfig()
  if (!config.public.mixpanelToken) {
    throw new Error('Mixpanel token is not defined in the runtime config.')
  }
  mixpanel.init(config.public.mixpanelToken, {
    api_host: config.public.mixpanelApiHost,
    persistence: 'localStorage',
    debug: isDebugEnabled(),
  })
  if (isDebugEnabled()) log.info('Mixpanel SDK initialized')
  const metadata = await extractFlagAndContextData()
  if (isDebugEnabled()) log.info('Mixpanel metadata:', metadata)
  mixpanel.register(metadata)

  // Set cohort as Mixpanel People profile property for reporting
  const cohortStr = metadata?.cohort ? String(metadata.cohort) : ''
  const targetinKeyStr = metadata?.targetingKey
    ? String(metadata.targetingKey)
    : ''
  if (cohortStr && targetinKeyStr) {
    mixpanel.identify(metadata.targetingKey)
    if (mixpanel.people.set_once) {
      mixpanel.people.set_once({ cohort: cohortStr })
    } else {
      mixpanel.people?.set({ cohort: cohortStr })
    }
  }

  isMixpanelSDKInitiated = true
  return mixpanel
}

function sendEventWithMixpanelSDK(
  name: string,
  data?: Record<string, unknown>
) {
  if (!import.meta.client) return

  return initializeMixpanelSDK()
    .then(() => {
      mixpanel.track(name, data)
    })
    .catch((error) => {
      if (isDebugEnabled()) {
        log.error(
          'Failed to send event to Mixpanel. The Mixpanel SDK is not initialized.',
          error
        )
      }
    })
}

export async function sendEvent(
  name: string,
  eventData?: Record<string, unknown>
): Promise<void> {
  if (isDebugEnabled()) log.info(`Sending Mixpanel event: ${name}`, eventData)
  if (!import.meta.client) return

  const metadata = await extractFlagAndContextData()

  const data = {
    ...eventData,
    split_test_data: metadata,
  }

  mixpanelLog(name, data)

  const { shift, chrome, onelaunch } = HostBrowserApis.getHostApis()

  const shiftAPI = shift?.analytics?.track
  if (shiftAPI) {
    shiftAPI(name, data)
    return
  }

  const oneLaunchAPI = onelaunch?.sendEvent
  if (oneLaunchAPI) {
    oneLaunchAPI(name, data)
    return
  }

  const chromeAPI = chrome?.runtime?.sendEvent
  if (chromeAPI) {
    chromeAPI(name, data)
    return
  }
  try {
    await sendEventWithMixpanelSDK(name, eventData)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    /**empty */
  }
}
